import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { waitlistSignupSchema } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { Logo, Wordmark } from '@/components/ui/Logo';
import { apiPost } from '@/lib/api';
import { MarketingFooter } from '@/features/marketing/MarketingPage';

type State = 'idle' | 'sending' | 'done' | 'error';

export function LandingPage() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>('idle');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [weddingDate, setWeddingDate] = useState('');
  const [company, setCompany] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = waitlistSignupSchema.safeParse({
      email,
      name: name || undefined,
      weddingDate: weddingDate || undefined,
      company: company || undefined,
      source: 'landing',
    });
    if (!parsed.success) {
      setState('error');
      return;
    }
    setState('sending');
    try {
      await apiPost('/public/waitlist', parsed.data, { auth: false });
      setState('done');
    } catch {
      setState('error');
    }
  }

  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-line">
        <div className="mx-auto flex w-full max-w-[1100px] items-center gap-3 px-4 py-4">
          <Logo size={32} tone="leaf" />
          <Wordmark className="text-leaf" />
          <Link
            to="/signin"
            className="ml-auto min-h-tap text-body text-leaf-soft underline-offset-4 hover:underline"
          >
            {t('landing.haveAccount')}
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1100px] px-4">
        <section className="grid gap-10 py-16 md:grid-cols-[1.1fr_1fr] md:py-24">
          <div>
            <p className="eyebrow">{t('brand.tagline')}</p>
            <h1 className="mt-4 font-display text-[44px] font-light leading-[1.1] md:text-[60px]">
              {t('landing.heroTitle')}
            </h1>
            <p className="mt-6 max-w-prose text-body text-leaf-soft">{t('landing.heroBody')}</p>
          </div>

          <div className="panel h-fit p-6">
            {state === 'done' ? (
              <p className="text-body">{t('landing.joined')}</p>
            ) : (
              <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
                <Field label={t('landing.emailLabel')}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  )}
                </Field>
                <Field label={t('landing.nameLabel')}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  )}
                </Field>
                <Field label={t('landing.dateLabel')}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      type="date"
                      value={weddingDate}
                      onChange={(e) => setWeddingDate(e.target.value)}
                    />
                  )}
                </Field>

                {/* Honeypot: hidden from people, irresistible to bots. */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="company">Company</label>
                  <input
                    id="company"
                    name="company"
                    tabIndex={-1}
                    autoComplete="off"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>

                {state === 'error' && (
                  <p role="alert" className="text-caption text-status-declinedFg">
                    {t('landing.failed')}
                  </p>
                )}

                <Button type="submit" loading={state === 'sending'}>
                  {state === 'sending' ? t('landing.joining') : t('landing.joinWaitlist')}
                </Button>
              </form>
            )}
          </div>
        </section>

        <hr className="rule" />

        <section className="grid gap-8 py-16 md:grid-cols-3">
          {(['Guests', 'Venues', 'Budget'] as const).map((key) => (
            <article key={key} className="flex flex-col gap-2">
              <h2 className="font-display text-subhead">{t(`landing.feature${key}Title`)}</h2>
              <p className="text-body text-leaf-soft">{t(`landing.feature${key}Body`)}</p>
            </article>
          ))}
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
